/**
 * Palantir PMS - Software para la gestión de la industria hotelera
 * Palantir PMS - Software for hotel industry management
 * Autor / Author: Alberto Castro Ull
 * Fecha de creación / Created on: 31/07/2025
 * Todos los derechos reservados / All rights reserved.
 * Este software es propiedad de Alberto Castro Ull.
 * This software is the property of Alberto Castro Ull.
 * Su uso está restringido a clientes con licencia emitida por Markulia Group, S.L.
 * Use is restricted to clients licensed by Markulia Group, S.L.
 */

import React, { useEffect, useState } from "react";
import axios from "../../api/axiosInstance";
import ChannexIntegrationCard from "./ChannexIntegrationCard";

const channelManagersCss = `
  .cm-page {
    width: 100%;
    max-width: 100%;
    padding: 0 18px 18px;
  }

  .cm-card {
    position: relative;
    border-radius: 18px;
    border: 1px solid transparent;
    background:
      linear-gradient(180deg, rgba(25, 25, 51, 0.94), rgba(10, 10, 22, 0.94)) padding-box,
      linear-gradient(135deg, rgba(48, 216, 255, 0.45), rgba(168, 85, 247, 0.52), rgba(255, 77, 79, 0.35)) border-box;
    box-shadow: 0 28px 90px rgba(0, 0, 0, 0.58);
    overflow: hidden;
  }

  .cm-card::before {
    content: "";
    position: absolute;
    inset: 0;
    background:
      radial-gradient(900px 300px at 10% 0%, rgba(48, 216, 255, 0.14), transparent 60%),
      radial-gradient(700px 260px at 90% 10%, rgba(168, 85, 247, 0.16), transparent 62%),
      radial-gradient(800px 260px at 40% 110%, rgba(255, 77, 79, 0.10), transparent 60%);
    pointer-events: none;
    opacity: 0.95;
  }

  .cm-card > * {
    position: relative;
    z-index: 1;
  }

  .cm-cardHeader {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    gap: 16px;
    padding: 18px 18px 14px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  }

  @media (max-width: 980px) {
    .cm-cardHeader {
      flex-direction: column;
      align-items: stretch;
    }
  }

  .cm-kicker {
    font-size: 0.74rem;
    text-transform: uppercase;
    letter-spacing: 0.14em;
    color: rgba(48, 216, 255, 0.76);
    margin-bottom: 6px;
  }

  .cm-title {
    margin: 0;
    font-size: 1.4rem;
    font-weight: 800;
    letter-spacing: 0.2px;
    color: transparent;
    background: linear-gradient(90deg, #e6e6f7 0%, rgba(48, 216, 255, 0.95) 40%, rgba(168, 85, 247, 0.95) 85%);
    -webkit-background-clip: text;
    background-clip: text;
  }

  .cm-subtitle {
    margin-top: 4px;
    font-size: 0.86rem;
    color: rgba(230, 230, 247, 0.72);
  }

  .cm-tabs {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
  }

  .cm-tab {
    border-radius: 12px;
    padding: 10px 14px;
    font-size: 0.86rem;
    font-weight: 700;
    border: 1px solid rgba(255, 255, 255, 0.14);
    cursor: pointer;
    color: rgba(230, 230, 247, 0.9);
    background: rgba(255, 255, 255, 0.04);
  }

  .cm-tab--active {
    color: #07131c;
    background: linear-gradient(135deg, rgba(48, 216, 255, 0.96), rgba(109, 236, 255, 0.92));
    border-color: rgba(48, 216, 255, 0.65);
  }

  .cm-cardBody {
    padding: 18px;
  }

  .cm-stack {
    display: grid;
    gap: 18px;
  }

  .cm-toolbar {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    gap: 14px;
    flex-wrap: wrap;
  }

  .cm-panelTitle {
    margin: 4px 0 0;
    font-size: 1.08rem;
    font-weight: 800;
    color: rgba(230, 230, 247, 0.96);
  }

  .cm-panelSubtitle {
    margin-top: 4px;
    color: rgba(230, 230, 247, 0.72);
    font-size: 0.84rem;
  }

  .cm-btn {
    border-radius: 12px;
    padding: 10px 14px;
    font-size: 0.86rem;
    font-weight: 700;
    border: 1px solid transparent;
    cursor: pointer;
  }

  .cm-btn--primary {
    color: #07131c;
    background: linear-gradient(135deg, rgba(48, 216, 255, 0.96), rgba(109, 236, 255, 0.92));
  }

  .cm-sectionCard {
    border-radius: 18px;
    border: 1px solid rgba(255, 255, 255, 0.08);
    background:
      linear-gradient(180deg, rgba(15, 18, 39, 0.96), rgba(8, 10, 24, 0.95)) padding-box,
      linear-gradient(135deg, rgba(48, 216, 255, 0.12), rgba(168, 85, 247, 0.18), rgba(255, 77, 79, 0.1)) border-box;
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.03), 0 16px 36px rgba(0, 0, 0, 0.22);
    overflow: hidden;
  }

  .cm-sectionHeader {
    padding: 16px 18px 12px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  }

  .cm-sectionKicker {
    font-size: 0.68rem;
    text-transform: uppercase;
    letter-spacing: 0.16em;
    color: rgba(48, 216, 255, 0.7);
    margin-bottom: 5px;
  }

  .cm-sectionTitle {
    margin: 0;
    font-size: 1rem;
    font-weight: 800;
    color: rgba(230, 230, 247, 0.96);
  }

  .cm-sectionSubtitle {
    margin-top: 4px;
    font-size: 0.79rem;
    color: rgba(230, 230, 247, 0.64);
  }

  .cm-sectionBody {
    padding: 14px 16px 16px;
  }

  .cm-list {
    display: grid;
    gap: 12px;
  }

  .cm-rowCard {
    border-radius: 16px;
    border: 1px solid rgba(255, 255, 255, 0.08);
    background: rgba(255, 255, 255, 0.04);
    padding: 12px 14px;
  }

  .cm-grid {
    display: grid;
    gap: 10px 14px;
  }

  .cm-grid--credentials {
    grid-template-columns: repeat(6, minmax(0, 1fr));
  }

  .cm-grid--mapping {
    grid-template-columns: minmax(160px, 1.3fr) repeat(2, minmax(90px, 0.7fr)) minmax(170px, 1fr) minmax(180px, 1fr) minmax(180px, 1fr);
  }

  .cm-grid--services {
    grid-template-columns: minmax(160px, 1.1fr) minmax(100px, 0.7fr) minmax(170px, 1fr) minmax(180px, 1fr);
  }

  @media (max-width: 1380px) {
    .cm-grid--credentials {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    .cm-grid--mapping,
    .cm-grid--services {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }

  @media (max-width: 860px) {
    .cm-grid--credentials,
    .cm-grid--mapping,
    .cm-grid--services {
      grid-template-columns: 1fr;
    }
  }

  .cm-fieldLabel {
    font-size: 0.67rem;
    text-transform: uppercase;
    letter-spacing: 0.14em;
    color: rgba(48, 216, 255, 0.7);
    margin-bottom: 4px;
  }

  .cm-fieldValue {
    min-height: 34px;
    display: flex;
    align-items: center;
    font-size: 0.88rem;
    color: rgba(230, 230, 247, 0.94);
    word-break: break-word;
  }

  .cm-fieldValue--mono {
    font-variant-numeric: tabular-nums;
  }

  .cm-fieldHint {
    margin-top: 4px;
    font-size: 0.72rem;
    color: rgba(230, 230, 247, 0.56);
  }

  .cm-providerPill {
    display: inline-flex;
    align-items: center;
    width: fit-content;
    padding: 6px 10px;
    border-radius: 999px;
    border: 1px solid rgba(255, 255, 255, 0.1);
    background: rgba(255, 255, 255, 0.06);
    font-size: 0.78rem;
    font-weight: 700;
    color: rgba(230, 230, 247, 0.88);
    text-transform: capitalize;
  }

  .cm-actionsRow {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid rgba(255, 255, 255, 0.08);
  }

  .cm-emptyState {
    padding: 14px 16px;
    border-radius: 14px;
    background: rgba(255, 255, 255, 0.04);
    color: rgba(230, 230, 247, 0.72);
  }

  .cm-tableWrap {
    overflow-x: auto;
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 16px;
    background: rgba(4, 6, 18, 0.35);
  }

  .cm-table {
    width: 100%;
    margin-bottom: 0;
    table-layout: fixed;
  }

  .cm-card .table thead th {
    background: linear-gradient(180deg, rgba(32, 24, 58, 0.96), rgba(18, 14, 36, 0.96));
    color: rgba(230, 230, 247, 0.92);
    border-color: rgba(255, 255, 255, 0.08);
    font-size: 0.72rem;
    font-weight: 800;
    text-transform: uppercase;
    padding: 10px 8px;
    white-space: normal;
    line-height: 1.25;
  }

  .cm-card .table tbody td {
    background: rgba(255, 255, 255, 0.02);
    color: #ffffff;
    border-color: rgba(255, 255, 255, 0.07);
    padding: 9px 8px;
    font-size: 0.82rem;
    vertical-align: middle;
  }

  .cm-card .table tbody tr:nth-child(even) td {
    background: rgba(255, 255, 255, 0.045);
  }

  .cm-cell--wrap {
    white-space: normal;
  }

  .cm-cell--mono {
    font-variant-numeric: tabular-nums;
  }

  .cm-cell--muted {
    color: rgba(230, 230, 247, 0.76);
  }

  .cm-cell--secret {
    letter-spacing: 0.18em;
  }

  .cm-cell--actions {
    white-space: nowrap;
    text-align: right;
  }

  .cm-card .table .cm-cell--actions .btn {
    padding: 0.25rem 0.45rem;
    font-size: 0.72rem;
  }

  .cm-col-id { width: 52px; }
  .cm-col-bu { width: 140px; }
  .cm-col-provider { width: 110px; }
  .cm-col-user { width: 170px; }
  .cm-col-secret { width: 88px; }
  .cm-col-system { width: 110px; }
  .cm-col-actions { width: 116px; }
  .cm-col-roomtype { width: 96px; }
  .cm-col-rateplan { width: 96px; }
  .cm-col-name { width: 180px; }
  .cm-col-category { width: 190px; }
  .cm-col-rate { width: 180px; }
  .cm-col-extra { width: 180px; }

  .cm-selectCompact {
    min-width: 0;
    font-size: 0.78rem;
    padding-top: 0.32rem;
    padding-bottom: 0.32rem;
  }

  .cm-card .form-select,
  .cm-card .form-control {
    background: rgba(16, 16, 38, 0.88);
    color: #ffffff;
    border: 1px solid rgba(255, 255, 255, 0.12);
  }

  .cm-stateCard {
    padding: 28px 22px;
  }

  .cm-stateTitle {
    font-size: 1rem;
    font-weight: 700;
    color: rgba(230, 230, 247, 0.92);
  }

  .cm-stateError {
    margin-top: 8px;
    color: #ff9a9a;
  }
`;

function ChannelManagers() {
  const [credentials, setCredentials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [businessUnits, setBusinessUnits] = useState([]);
  const [activeTab, setActiveTab] = useState("lighthouse");

  const getBusinessUnitLabel = (businessUnitId) => {
    const match = businessUnits.find((bu) => String(bu.id) === String(businessUnitId));
    return match?.name || `BU ${businessUnitId}`;
  };

useEffect(() => {
  axios.get("/api/business-units")
    .then(res => {
      setBusinessUnits(res.data);
    })
    .catch(err => {
      console.error("? Error cargando Business Units:", err);
    });
}, []);

const [mappings, setMappings] = useState([]);
const [roomCategories, setRoomCategories] = useState([]);
const [roomRates, setRoomRates] = useState([]);
const [serviceMappings, setServiceMappings] = useState([]);
const [extras, setExtras] = useState([]);


// [CM][MAP] Load ALL BUs mappings + all categories/rates (cascading handled per row)
const fetchMappings = async () => {
  try {
    console.log("[CM][MAP][LOAD] loading all BU mappings…");
    const res = await axios.get(`/api/admin/channel-manager/mappings`);
    setMappings(res.data);

    const [cats, rates] = await Promise.all([
      axios.get(`/api/room_categories`),
      axios.get(`/api/roomRates/all`)
    ]);
    setRoomCategories(cats.data);
    setRoomRates(rates.data);

    console.log("[CM][MAP][LOAD][OK] rows=%d cats=%d rates=%d",
      res.data.length, cats.data.length, rates.data.length);
  } catch (err) {
    console.error("? Error cargando mappings:", err);
  }
};
const fetchServiceMappings = async () => {
  try {
    console.log("[CM][SERVICES][LOAD] loading all BU service mappings…");

    const res = await axios.get(`/api/admin/channel-manager/services-mappings`);
    console.log("?? [FRONT][RAW RESPONSE]", res.data);

    setServiceMappings(res.data);

    // ?? detectar todas las BUs únicas en mappings
    const buIds = [...new Set(res.data.map((m) => m.business_unit_id))];
    console.log("?? [FRONT][DETECTED BU IDs]", buIds);

    let allExtras = [];
    for (const buId of buIds) {
      console.log("?? [FRONT][FETCH EXTRAS] BU =", buId);
      try {
        const ex = await axios.get(`/api/admin/channel-manager/${buId}/extras`);
        console.log("   ? fetched extras:", ex.data.length);
        allExtras = [...allExtras, ...ex.data];
      } catch (err) {
        console.warn("   ?? Error fetching extras for BU", buId, err.message);
      }
    }

    setExtras(allExtras);
    console.log("[CM][SERVICES][LOAD][OK] rows=%d extras=%d", res.data.length, allExtras.length);
  } catch (err) {
    console.error("? [FRONT][SERVICES][LOAD][ERROR]", err);
  }
};



const handleUpdateMapping = async (mappingId, update) => {
  try {
    console.log("[CM][MAP][PATCH] id=%s payload=%o", mappingId, update);
    await axios.patch(`/api/admin/channel-manager/mappings/${mappingId}`, update);
    // Always refresh all mappings to reflect latest values
    await fetchMappings();
    console.log("[CM][MAP][PATCH][OK] id=%s", mappingId);
  } catch (err) {
    console.error("? Error actualizando mapping:", err);
    alert("Error updating mapping");
  }
};

const handleUpdateServiceMapping = async (mappingId, update) => {
  try {
    console.log("[CM][SERVICES][PATCH] id=%s payload=%o", mappingId, update);
    await axios.patch(`/api/admin/channel-manager/services-mappings/${mappingId}`, update);
    await fetchServiceMappings();
    console.log("[CM][SERVICES][PATCH][OK] id=%s", mappingId);
  } catch (err) {
    console.error("? Error actualizando service mapping:", err);
    alert("Error updating service mapping");
  }
};





  // Modal
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [formData, setFormData] = useState({
    business_unit_id: "",
    provider: "lighthouse",
    username: "",
    password: "",
    system_id: "",
  });

  useEffect(() => {
    fetchData();
    fetchMappings();
    fetchServiceMappings();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get("/api/admin/channel-manager-credentials");
      setCredentials(res.data);
    } catch (err) {
      console.error("? Error cargando credenciales:", err);
      setError("Error loading Channel Manager credentials");
    } finally {
      setLoading(false);
    }
  };

  const openModal = (cred = null) => {
    if (cred) {
      setEditId(cred.id);
      setFormData({
        business_unit_id: cred.business_unit_id,
        provider: cred.provider,
        username: cred.username,
        password: cred.password,
        system_id: cred.system_id || "",
      });
    } else {
      setEditId(null);
      setFormData({
        business_unit_id: "",
        provider: "lighthouse",
        username: "",
        password: "",
        system_id: "",
      });
    }
    setShowModal(true);
  };

  const handleSave = async () => {
    try {
      if (editId) {
        await axios.put(`/api/admin/channel-manager-credentials/${editId}`, formData);
      } else {
        await axios.post("/api/admin/channel-manager-credentials", formData);
      }
      setShowModal(false);
      fetchData();
    } catch (err) {
      console.error("? Error guardando credencial:", err);
      alert("Error saving credentials");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this credential?")) return;
    try {
      await axios.delete(`/api/admin/channel-manager-credentials/${id}`);
      fetchData();
    } catch (err) {
      console.error("? Error eliminando credencial:", err);
      alert("Error deleting credentials");
    }
  };

  if (loading) {
    return (
      <div className="cm-page">
        <style>{channelManagersCss}</style>
        <div className="cm-card cm-stateCard">
          <div className="cm-stateTitle">Loading channel managers...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="cm-page">
        <style>{channelManagersCss}</style>
        <div className="cm-card cm-stateCard">
          <div className="cm-stateTitle">Could not load channel managers.</div>
          <div className="cm-stateError">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="cm-page">
      <style>{channelManagersCss}</style>

      <div className="cm-card">
        <div className="cm-cardHeader">
          <div>
            <div className="cm-kicker">Admin &gt; Integrations</div>
            <h2 className="cm-title">Channel Managers</h2>
            <div className="cm-subtitle">
              {activeTab === "lighthouse"
                ? "Manage Lighthouse credentials, room mappings and service mappings from Pilcore."
                : "Review the isolated Channex staging connection without mixing it with existing providers."}
            </div>
          </div>

          <div className="cm-tabs" role="tablist" aria-label="Channel manager providers">
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "lighthouse"}
              className={`cm-tab ${activeTab === "lighthouse" ? "cm-tab--active" : ""}`}
              onClick={() => setActiveTab("lighthouse")}
            >
              Lighthouse
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "channex"}
              className={`cm-tab ${activeTab === "channex" ? "cm-tab--active" : ""}`}
              onClick={() => setActiveTab("channex")}
            >
              Channex
            </button>
          </div>
        </div>

        <div className="cm-cardBody">
          {activeTab === "lighthouse" ? (
            <div className="cm-stack">
              <div className="cm-toolbar">
                <div>
                  <div className="cm-kicker">Provider</div>
                  <h3 className="cm-panelTitle">Lighthouse</h3>
                  <div className="cm-panelSubtitle">
                    Credentials and mappings currently connected to Pilcore.
                  </div>
                </div>

                <button className="cm-btn cm-btn--primary" onClick={() => openModal()}>
                  + Add Credential
                </button>
              </div>

              <div className="cm-sectionCard">
                <div className="cm-sectionHeader">
                  <div className="cm-sectionKicker">Credentials</div>
                  <h2 className="cm-sectionTitle">Channel Manager Credentials</h2>
                  <div className="cm-sectionSubtitle">
                    Stored provider access for each business unit.
                  </div>
                </div>
                <div className="cm-sectionBody">
                  {credentials.length === 0 ? (
                    <div className="cm-emptyState">No credentials configured yet.</div>
                  ) : (
                    <div className="cm-list">
                      {credentials.map((c) => (
                        <div className="cm-rowCard" key={c.id}>
                          <div className="cm-grid cm-grid--credentials">
                            <div>
                              <div className="cm-fieldLabel">ID</div>
                              <div className="cm-fieldValue cm-fieldValue--mono">{c.id}</div>
                            </div>

                            <div>
                              <div className="cm-fieldLabel">Business Unit</div>
                              <div className="cm-fieldValue">{getBusinessUnitLabel(c.business_unit_id)}</div>
                            </div>

                            <div>
                              <div className="cm-fieldLabel">Provider</div>
                              <div className="cm-fieldValue">
                                <span className="cm-providerPill">{c.provider}</span>
                              </div>
                            </div>

                            <div>
                              <div className="cm-fieldLabel">Username</div>
                              <div className="cm-fieldValue">{c.username || "-"}</div>
                              <div className="cm-fieldHint">Lighthouse: @ID when Type="1"</div>
                            </div>

                            <div>
                              <div className="cm-fieldLabel">Password</div>
                              <div className="cm-fieldValue cm-cell--secret">******</div>
                              <div className="cm-fieldHint">Lighthouse: @MessagePassword</div>
                            </div>

                            <div>
                              <div className="cm-fieldLabel">System ID</div>
                              <div className="cm-fieldValue cm-fieldValue--mono">{c.system_id || "-"}</div>
                              <div className="cm-fieldHint">Lighthouse: @ID when Type="2"</div>
                            </div>
                          </div>

                          <div className="cm-actionsRow">
                            <button className="btn btn-sm btn-primary" onClick={() => openModal(c)}>
                              Edit
                            </button>
                            <button className="btn btn-sm btn-danger" onClick={() => handleDelete(c.id)}>
                              Delete
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="cm-sectionCard">
                <div className="cm-sectionHeader">
                  <div className="cm-sectionKicker">Mapping</div>
                  <h3 className="cm-sectionTitle">Room / Rate Mapping</h3>
                  <div className="cm-sectionSubtitle">
                    Compact view of Lighthouse room types and rate plans linked to Pilcore.
                  </div>
                </div>
                <div className="cm-sectionBody">
                  {mappings.length === 0 ? (
                    <div className="cm-emptyState">No room or rate mappings available.</div>
                  ) : (
                    <div className="cm-list">
                      {mappings.map((m) => {
                        const buCategories = roomCategories.filter(
                          (rc) => rc.business_unit_id === m.business_unit_id
                        );
                        const buRates = roomRates.filter(
                          (rr) => rr.room_category?.business_unit_id === m.business_unit_id
                        );
                        const categoryRates = m.room_category_id
                          ? buRates.filter((rr) => rr.room_category.id === m.room_category_id)
                          : [];

                        return (
                          <div className="cm-rowCard" key={m.id}>
                            <div className="cm-grid cm-grid--mapping">
                              <div>
                                <div className="cm-fieldLabel">Business Unit</div>
                                <div className="cm-fieldValue">{m.business_unit?.name || m.business_unit_id}</div>
                              </div>

                              <div>
                                <div className="cm-fieldLabel">RoomType</div>
                                <div className="cm-fieldValue cm-fieldValue--mono">{m.room_type_id}</div>
                              </div>

                              <div>
                                <div className="cm-fieldLabel">RatePlan</div>
                                <div className="cm-fieldValue cm-fieldValue--mono">{m.rate_plan_id}</div>
                              </div>

                              <div>
                                <div className="cm-fieldLabel">Name</div>
                                <div className="cm-fieldValue">{m.name}</div>
                              </div>

                              <div>
                                <div className="cm-fieldLabel">PMS Category</div>
                                <select
                                  className="form-select form-select-sm bg-dark text-light cm-selectCompact"
                                  value={m.room_category_id || ""}
                                  onChange={(e) =>
                                    handleUpdateMapping(m.id, { room_category_id: e.target.value, room_rate_id: null })
                                  }
                                >
                                  <option value="">-- Select Category --</option>
                                  {buCategories.map((rc) => (
                                    <option key={rc.id} value={rc.id}>
                                      {rc.name}
                                    </option>
                                  ))}
                                </select>
                              </div>

                              <div>
                                <div className="cm-fieldLabel">PMS Rate</div>
                                <select
                                  className="form-select form-select-sm bg-dark text-light cm-selectCompact"
                                  value={m.room_rate_id || ""}
                                  onChange={(e) => handleUpdateMapping(m.id, { room_rate_id: e.target.value })}
                                  disabled={!m.room_category_id}
                                >
                                  <option value="">-- Select Rate --</option>
                                  {categoryRates.map((rr) => (
                                    <option key={rr.id} value={rr.id}>
                                      {rr.name}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              <div className="cm-sectionCard">
                <div className="cm-sectionHeader">
                  <div className="cm-sectionKicker">Mapping</div>
                  <h3 className="cm-sectionTitle">Services Mapping</h3>
                  <div className="cm-sectionSubtitle">
                    Quick mapping between Lighthouse service codes and Pilcore extras.
                  </div>
                </div>
                <div className="cm-sectionBody">
                  {serviceMappings.length === 0 ? (
                    <div className="cm-emptyState">No service mappings available.</div>
                  ) : (
                    <div className="cm-list">
                      {serviceMappings.map((m) => {
                        const buExtras = extras.filter((ex) => ex.business_unit_id === m.business_unit_id);

                        return (
                          <div className="cm-rowCard" key={m.id}>
                            <div className="cm-grid cm-grid--services">
                              <div>
                                <div className="cm-fieldLabel">Business Unit</div>
                                <div className="cm-fieldValue">{m.business_unit?.name || m.business_unit_id}</div>
                              </div>

                              <div>
                                <div className="cm-fieldLabel">Service Code</div>
                                <div className="cm-fieldValue cm-fieldValue--mono">{m.service_inventory_code}</div>
                              </div>

                              <div>
                                <div className="cm-fieldLabel">Name</div>
                                <div className="cm-fieldValue">{m.name}</div>
                              </div>

                              <div>
                                <div className="cm-fieldLabel">PMS Extra</div>
                                <select
                                  className="form-select form-select-sm bg-dark text-light cm-selectCompact"
                                  value={m.extra_id || ""}
                                  onChange={(e) => handleUpdateServiceMapping(m.id, { extra_id: e.target.value })}
                                >
                                  <option value="">-- Select Extra --</option>
                                  {buExtras.map((ex) => (
                                    <option key={ex.id} value={ex.id}>
                                      {ex.name}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <ChannexIntegrationCard embedded businessUnits={businessUnits} />
          )}
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div
            style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0,0,0,0.6)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1050,
            }}
        >
            <div style={{ zoom: 0.9, width: "600px", maxWidth: "95%" }}>
            <div
                style={{
                backgroundColor: "#1a1a1a",
                color: "white",
                padding: "20px",
                borderRadius: "6px",
                }}
            >
                <h2 className="text-warning text-center mb-4">
                {editId ? "Edit Credential" : "Add Credential"}
                </h2>

                <div className="mb-3">
                    <label className="form-label">Business Unit</label>
                    <select
                        className="form-select"
                        style={{ backgroundColor: "#333", color: "white", border: "1px solid #555" }}
                        value={formData.business_unit_id}
                        onChange={(e) => setFormData({ ...formData, business_unit_id: e.target.value })}
                    >
                        <option value="">-- Select Business Unit --</option>
                        {businessUnits.map((bu) => (
                        <option key={bu.id} value={bu.id}>
                            {bu.name || `BU ${bu.id}`}
                        </option>
                        ))}
                    </select>
                    </div>


                <div className="mb-3">
                <label className="form-label">Provider</label>
                <select
                    className="form-select"
                    style={{ backgroundColor: "#333", color: "white", border: "1px solid #555" }}
                    value={formData.provider}
                    onChange={(e) => setFormData({ ...formData, provider: e.target.value })}
                >
                    <option value="lighthouse">Lighthouse</option>
                    <option value="siteminder">SiteMinder</option>
                    <option value="dingus">Dingus</option>
                    <option value="other">Other</option>
                </select>
                </div>

                <div className="mb-3">
                    <label className="form-label">
                        Username
                        <div style={{ fontSize: "0.75rem", color: "#aaa" }}>
                        (Lighthouse: <code>@ID</code> when Type="1")
                        </div>
                    </label>
                    <input
                        type="text"
                        className="form-control"
                        style={{ backgroundColor: "#333", color: "white", border: "1px solid #555" }}
                        value={formData.username}
                        onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    />
                    </div>


                <div className="mb-3">
                    <label className="form-label">
                        Password
                        <div style={{ fontSize: "0.75rem", color: "#aaa" }}>
                        (Lighthouse: <code>@MessagePassword</code> when Type="1")
                        </div>
                    </label>
                    <input
                        type="password"
                        className="form-control"
                        style={{ backgroundColor: "#333", color: "white", border: "1px solid #555" }}
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    />
                    </div>


                <div className="mb-3">
                    <label className="form-label">
                        System ID
                        <div style={{ fontSize: "0.75rem", color: "#aaa" }}>
                        (Lighthouse: <code>@ID</code> when Type="2")
                        </div>
                    </label>
                    <input
                        type="text"
                        className="form-control"
                        style={{ backgroundColor: "#333", color: "white", border: "1px solid #555" }}
                        value={formData.system_id}
                        onChange={(e) => setFormData({ ...formData, system_id: e.target.value })}
                    />
                    </div>


                <div className="d-flex justify-content-end">
                <button className="btn btn-secondary me-2" onClick={() => setShowModal(false)}>
                    Cancel
                </button>
                <button className="btn btn-warning" onClick={handleSave}>
                    Save
                </button>
                </div>
            </div>
            </div>
        </div>
        )}

    </div>
  );
}

export default ChannelManagers;

